import { createRound } from "../init-game";
import type {
  ConfirmRoundResultAction,
  FruitPriceWon,
  FruitProductId,
  FruitRoundResult,
  FruitShopV2Player,
  FruitShopV2State,
  ReadyForPriceSubmissionAction,
  RevealRoundAction,
  StartExtensionRoundAction,
  SubmitPricesAction,
  UseChangeAction,
  UseSecretAction,
} from "../types";
import {
  EXTENSION_PENALTY_WON,
  FRUIT_PRODUCT_IDS,
  type FruitShopV2Round,
} from "../types";
import { resolveProductPricing } from "../utils/pricing";

function updatePlayer(
  state: FruitShopV2State,
  playerId: string,
  update: (player: FruitShopV2Player) => FruitShopV2Player,
): readonly FruitShopV2Player[] {
  return state.players.map((player) =>
    player.playerId === playerId ? update(player) : player,
  );
}

function addReadyPlayer(
  state: FruitShopV2State,
  playerId: string,
): FruitShopV2Round {
  const itemReadyPlayerIds = [
    ...new Set([...state.currentRound.itemReadyPlayerIds, playerId]),
  ];
  const allReady = itemReadyPlayerIds.length === state.players.length;

  return {
    ...state.currentRound,
    itemReadyPlayerIds,
    phase: allReady ? "PRICE_SUBMISSION" : state.currentRound.phase,
  };
}

function getSellerIds(
  state: FruitShopV2State,
  productId: FruitProductId,
): readonly string[] {
  return state.players
    .filter((player) => player.fruitRights.includes(productId))
    .map((player) => player.playerId);
}

function settleResults(state: FruitShopV2State): readonly FruitRoundResult[] {
  const hiddenProductIds = new Set(
    Object.values(state.currentRound.secretSelections),
  );
  const results: FruitRoundResult[] = [];

  for (const productId of FRUIT_PRODUCT_IDS) {
    const sellerIds = getSellerIds(state, productId);
    if (sellerIds.length === 0) {
      continue;
    }

    const sellerPrices = sellerIds.map((playerId) => ({
      playerId,
      priceWon: state.currentRound.priceSubmissions[playerId]?.[
        productId
      ] as FruitPriceWon,
    }));

    const result = resolveProductPricing({
      productId,
      sellerPrices,
      sellerCount: sellerIds.length,
      hidden: hiddenProductIds.has(productId),
    });
    if (result) {
      results.push(result);
    }
  }

  return results;
}

function buildRoundIncome(
  playerIds: readonly string[],
  results: readonly FruitRoundResult[],
): Readonly<Record<string, number>> {
  const incomeByPlayerId = Object.fromEntries(
    playerIds.map((playerId) => [playerId, 0]),
  );

  for (const result of results) {
    for (const [playerId, payoutWon] of Object.entries(
      result.payoutByPlayerId,
    )) {
      incomeByPlayerId[playerId] =
        (incomeByPlayerId[playerId] ?? 0) + payoutWon;
    }
  }

  return incomeByPlayerId;
}

function getGarnetAward(totalIncomeWon: number): number {
  if (totalIncomeWon >= 50000) {
    return 2;
  }
  if (totalIncomeWon >= 40000) {
    return 1;
  }
  return 0;
}

function finalizeGame(state: FruitShopV2State): FruitShopV2State {
  const totals = state.players.map((player) => player.totalIncomeWon);
  const allTied = totals.every((total) => total === totals[0]);

  if (allTied) {
    const players = state.players.map((player) => ({
      ...player,
      totalIncomeWon: player.totalIncomeWon - EXTENSION_PENALTY_WON,
    }));

    return {
      ...state,
      players,
      currentRound: {
        ...state.currentRound,
        phase: "EXTENSION_READY",
      },
      completedRounds: [...state.completedRounds, state.currentRound],
    };
  }

  const maxIncome = Math.max(...totals);
  const minIncome = Math.min(...totals);
  const winnerIds = state.players
    .filter((player) => player.totalIncomeWon === maxIncome)
    .map((player) => player.playerId);
  const lowestPlayerIds = state.players
    .filter((player) => player.totalIncomeWon === minIncome)
    .map((player) => player.playerId);

  return {
    ...state,
    status: "COMPLETED",
    currentRound: {
      ...state.currentRound,
      phase: "COMPLETED",
    },
    completedRounds: [...state.completedRounds, state.currentRound],
    winnerIds,
    lowestPlayerIds,
    garnetAwardsByPlayerId: Object.fromEntries(
      state.players.map((player) => [
        player.playerId,
        getGarnetAward(player.totalIncomeWon),
      ]),
    ),
    lifeTokenAwardsByPlayerId: Object.fromEntries(
      state.players.map((player) => [
        player.playerId,
        winnerIds.length === 1 && winnerIds[0] === player.playerId ? 2 : 0,
      ]),
    ),
  };
}

function advanceAfterRound(state: FruitShopV2State): FruitShopV2State {
  if (state.currentRound.roundNumber >= state.roundCount) {
    return finalizeGame(state);
  }

  return {
    ...state,
    completedRounds: [...state.completedRounds, state.currentRound],
    currentRound: createRound(
      state.players,
      state.currentRound.roundNumber + 1,
    ),
  };
}

export function applyReadyForPriceSubmission(
  state: FruitShopV2State,
  action: ReadyForPriceSubmissionAction,
): FruitShopV2State {
  return {
    ...state,
    currentRound: addReadyPlayer(state, action.playerId),
  };
}

export function applySubmitPrices(
  state: FruitShopV2State,
  action: SubmitPricesAction,
): FruitShopV2State {
  return {
    ...state,
    currentRound: {
      ...state.currentRound,
      priceSubmissions: {
        ...state.currentRound.priceSubmissions,
        [action.playerId]: action.prices,
      },
    },
  };
}

export function applyUseSecret(
  state: FruitShopV2State,
  action: UseSecretAction,
): FruitShopV2State {
  const currentRound = {
    ...state.currentRound,
    secretSelections: {
      ...state.currentRound.secretSelections,
      [action.playerId]: action.productId,
    },
  };
  const withSecret = {
    ...state,
    players: updatePlayer(state, action.playerId, (player) => ({
      ...player,
      itemStatus: "USED_SECRET",
    })),
    currentRound,
  };

  if (state.currentRound.phase !== "ITEM_WINDOW") {
    return withSecret;
  }

  return {
    ...withSecret,
    currentRound: addReadyPlayer(withSecret, action.playerId),
  };
}

export function applyUseChange(
  state: FruitShopV2State,
  action: UseChangeAction,
): FruitShopV2State {
  const players = updatePlayer(state, action.playerId, (player) => ({
    ...player,
    fruitRights: player.fruitRights.map((productId) =>
      productId === action.fromProductId ? action.toProductId : productId,
    ),
    itemStatus: "USED_CHANGE",
  }));
  const withChange = {
    ...state,
    players,
    currentRound: {
      ...state.currentRound,
      changeRecords: [
        ...state.currentRound.changeRecords,
        {
          playerId: action.playerId,
          fromProductId: action.fromProductId,
          toProductId: action.toProductId,
        },
      ],
    },
  };

  return {
    ...withChange,
    currentRound: {
      ...addReadyPlayer(withChange, action.playerId),
      market: createRound(players, state.currentRound.roundNumber).market,
    },
  };
}

export function applyRevealRound(
  state: FruitShopV2State,
  _action: RevealRoundAction,
): FruitShopV2State {
  const productResults = settleResults(state);
  const incomeByPlayerId = buildRoundIncome(
    state.players.map((player) => player.playerId),
    productResults,
  );
  const players = state.players.map((player) => ({
    ...player,
    totalIncomeWon:
      player.totalIncomeWon + (incomeByPlayerId[player.playerId] ?? 0),
  }));

  return {
    ...state,
    players,
    currentRound: {
      ...state.currentRound,
      phase: "ROUND_REVEALED",
      productResults,
      incomeByPlayerId,
      confirmedPlayerIds: [],
    },
  };
}

export function applyConfirmRoundResult(
  state: FruitShopV2State,
  action: ConfirmRoundResultAction,
): FruitShopV2State {
  const confirmedPlayerIds = [
    ...new Set([...state.currentRound.confirmedPlayerIds, action.playerId]),
  ];
  const confirmedState = {
    ...state,
    currentRound: {
      ...state.currentRound,
      confirmedPlayerIds,
    },
  };

  if (confirmedPlayerIds.length < state.players.length) {
    return confirmedState;
  }

  return advanceAfterRound(confirmedState);
}

export function applyStartExtensionRound(
  state: FruitShopV2State,
  _action: StartExtensionRoundAction,
): FruitShopV2State {
  return {
    ...state,
    currentRound: createRound(
      state.players,
      state.currentRound.roundNumber + 1,
    ),
    extensionNumber: state.extensionNumber + 1,
  };
}
